import { FC, useEffect } from 'react';

import { FileProcessing } from '@/_app/layouts/FileProcessing';
import { clientConfig } from '@/config/clientConfig';
import { ApiJobResultEntry, useApi } from '@/shared/api';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { useSjActions } from '@/shared/lib/sj-orm/hooks/useSjActions';
import { FileMetaInfoDto } from '@/shared/lib/sj-orm/models/file-meta-info.dto';
import { useProfileStore } from '@/shared/store/profile.store';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { useNotices } from '../notices/useNotices';

import { useSiActions } from './hooks/useSiActions';
import { useSmartImportStore } from './stores/smartImport.store';
import { makeEntities } from './helpers';
import { JobStatus } from './types';

export const SmartImportContainer: FC = () => {
  const sjdb = useSJDatabase();
  const jobId = useSmartImportStore((s) => s.jobId);
  const siStore = useSmartImportStore();
  const siActions = useSiActions();
  const api = useApi();
  const modals = useModalsStore();
  const sjFiles = useSjActions('fileMetaInfos');
  const { isBeneficiary, isAssistant } = useProfileStore();
  const notices = useNotices();

  const getEntriesData = (results: ApiJobResultEntry[]) => {
    const files: FileMetaInfoDto[] = [];
    const idFilePathMap: Record<string, string> = {};

    const items = results.flatMap((r) => {
      const file = sjFiles.findById(r.file_id ?? '');

      if (r.file_id && file?.filePath) {
        files.push(file);
        idFilePathMap[r.file_id] = file.filePath;
      }

      return r.results;
    });

    return {
      files,
      idFilePathMap,
      items,
    };
  };

  useEffect(() => {
    if (clientConfig.isExported) {
      return undefined;
    }
    if (!sjdb.isDbReady) return undefined;

    const fetchPendingJob = async () => {
      if (isBeneficiary || isAssistant) return;
      const { data } = await api.aiGetJobs();
      const pendingJob = data?.jobs?.find((job) => job.status === JobStatus.PENDING);
      siStore.setJobId(pendingJob?.job_id ?? null);
    };

    const runJob = async () => {
      if (!jobId) return;

      const { data: initialJobData } = await api.aiGetJob(jobId);
      if (!initialJobData) return;
      siStore.setData(initialJobData);

      if (initialJobData.status === JobStatus.DONE) {
        siActions.resetStores();
        return;
      }

      siActions.startPolling(jobId, (data) => {
        const state = useSmartImportStore.getState();
        if (data.done === state.data?.done) return;
        useSmartImportStore.setState((prev) => ({ ...prev, data }));
      });
    };

    if (jobId) {
      runJob();
    } else {
      fetchPendingJob();
    }

    return () => siActions.stopPolling();
  }, [sjdb.isDbReady, jobId, isBeneficiary]);

  useEffect(() => {
    if (siStore.data?.status !== JobStatus.DONE) return;
    siActions.stopPolling();

    const createAndShow = async () => {
      if (!siStore.data?.results) return;
      const { files, items, idFilePathMap } = getEntriesData(siStore.data.results);

      const entities = makeEntities(items, idFilePathMap);
      siActions.saveEntities(entities);

      if (!siStore.data) return;
      modals.openModal({
        type: 'SmartImportSuccessModal',
        total: siStore.data.total,
        failed: siStore.data.errors.length,
        done: siStore.data.done,
        assets: entities.assets.length,
        personalIds: entities.personalIdentifiers.length,
        personalDocuments: entities.privateDocuments.length,
        contacts: entities.contacts.length,
        onClose: () => {
          files.forEach((item) => {
            sjFiles.update({
              ...item,
              isFileAttached: true,
            });
          });

          notices.success('File(s) attached');
        },
      });

      siActions.resetStores();
    };

    createAndShow();
  }, [siStore.data?.status]);

  if (siStore.data?.status !== JobStatus.PENDING) {
    return null;
  }

  return <FileProcessing />;
};
